/**
 * Helpers for staff inventory add/edit (tags, highlights, images, specs).
 */

export function stringArrayToLines(arr) {
  if (!Array.isArray(arr) || !arr.length) return '';
  return arr.map((x) => (typeof x === 'string' ? x : String(x || ''))).join('\n');
}

export function lineSeparatedToStringArray(text) {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const isHttpUrl = (s) => typeof s === 'string' && /^https?:\/\//i.test(s.trim());

/** Same pattern as backend `buildAutoAlt` (productImageS3) for defaults before save. */
export function defaultProductPhotoAlt(productName) {
  const name = String(productName || '').replace(/\s+/g, ' ').trim();
  const label = name || 'Rental inventory item';
  const alt = `${label} — rental product photo`;
  return alt.length > 200 ? alt.slice(0, 197) + '…' : alt;
}

/**
 * Matches backend normalizeCatalogImageUrl: bare host/path (no scheme) and scheme-relative URLs.
 */
export function normalizeCatalogImageUrlFrontend(raw) {
  if (raw == null) return null;
  const u = String(raw).trim();
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('//')) return `https:${u}`;
  if (u.startsWith('/') || !u.includes('/')) return null;
  const hostSegment = u.split('/')[0] || '';
  if (!hostSegment || /:/.test(hostSegment)) return null;
  return `https://${u}`;
}

/** Collect https URLs only (one per line) from textarea. */
export function imageUrlsFromMultiline(text) {
  return String(text || '')
    .split(/\n/)
    .map((s) => s.trim())
    .filter(isHttpUrl);
}

/**
 * Build payload for POST/PUT inventory `images`: array of `{ url }` or `{ url, alt }`.
 */
export function buildImagesPayload(multilineUrls, altByUrl) {
  const lines = imageUrlsFromMultiline(multilineUrls);
  const map = altByUrl && typeof altByUrl === 'object' ? altByUrl : {};
  return lines.map((url) => {
    const alt = map[url] != null ? String(map[url]).trim() : '';
    return alt ? { url, alt } : { url };
  });
}

/** @deprecated Prefer buildImagesPayload; kept for callers that send URL-only arrays. */
export function imagesFromLines(text) {
  return buildImagesPayload(text, {});
}

export function appendUrlsToMultiline(baseText, urls) {
  const existing = String(baseText || '')
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const incoming = (Array.isArray(urls) ? urls : [])
    .map((u) => String(u || '').trim())
    .filter(isHttpUrl);
  const merged = [...existing];
  for (const url of incoming) {
    if (!merged.includes(url)) merged.push(url);
  }
  return merged.join('\n');
}

/** API may return legacy string URLs or `{ url, alt }`. */
export function imagesApiToMultiline(images) {
  if (!Array.isArray(images)) return '';
  const urls = [];
  for (const x of images) {
    if (typeof x === 'string' && isHttpUrl(x)) urls.push(x.trim());
    else if (x && typeof x === 'object' && typeof x.url === 'string' && isHttpUrl(x.url)) {
      urls.push(x.url.trim());
    }
  }
  return urls.join('\n');
}

export function extractImageAltMap(images) {
  const m = {};
  if (!Array.isArray(images)) return m;
  for (const x of images) {
    if (x && typeof x === 'object' && typeof x.url === 'string' && x.alt) {
      const alt = String(x.alt).trim();
      if (alt) m[x.url.trim()] = alt;
    }
  }
  return m;
}

/** Hydrate UI rows from GET /inventory/:id (staff). */
export function catalogPhotosFromInventoryApi(images) {
  if (!Array.isArray(images)) return [];
  const rows = [];
  let i = 0;
  for (const x of images) {
    if (typeof x === 'string') {
      const url = normalizeCatalogImageUrlFrontend(x);
      if (!url) continue;
      rows.push({
        clientId: `legacy:${i}:${url.slice(-40)}`,
        url,
        alt: '',
        s3Key: null,
      });
      i += 1;
      continue;
    }
    if (x && typeof x === 'object' && typeof x.url === 'string') {
      const url = normalizeCatalogImageUrlFrontend(x.url);
      if (!url) continue;
      const alt = typeof x.alt === 'string' ? x.alt.trim() : '';
      const rawKey = typeof x.s3Key === 'string' ? x.s3Key.trim() : '';
      const s3Key = rawKey.startsWith('products/') && !rawKey.includes('..') ? rawKey : null;
      rows.push({
        clientId: `${url}::${s3Key || i}`,
        url,
        alt,
        s3Key,
      });
      i += 1;
    }
  }
  return rows;
}

/** POST/PUT body fragments for catalog photos (upload-only workflow). */
export function buildInventoryImagesSubmitBody(catalogPhotos, pendingRemovedS3Keys, productNameForDefaultAlt = '') {
  const fallbackAlt = defaultProductPhotoAlt(productNameForDefaultAlt);
  const images = (catalogPhotos || [])
    .map((p) => {
      const url = normalizeCatalogImageUrlFrontend(p.url);
      if (!url) return null;
      const custom = p.alt != null && String(p.alt).trim() ? String(p.alt).trim() : '';
      const row = { url, alt: custom || fallbackAlt };
      if (p.s3Key != null && String(p.s3Key).trim().startsWith('products/')) {
        row.s3Key = String(p.s3Key).trim();
      }
      return row;
    })
    .filter(Boolean);
  const removedProductS3Keys = [...new Set((pendingRemovedS3Keys || []).map((k) => String(k || '').trim()))].filter(
    (k) => k.startsWith('products/') && !k.includes('..')
  );
  return { images, removedProductS3Keys };
}

export function specRowsToPayload(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows
    .map((r) => ({
      label: (r.label && String(r.label).trim()) || '',
      value: (r.value && String(r.value).trim()) || '',
    }))
    .filter((r) => r.label && r.value);
}

export function specPayloadToRows(specs) {
  if (!Array.isArray(specs) || !specs.length) {
    return [{ label: '', value: '' }];
  }
  const out = specs
    .map((s) => {
      if (s && typeof s === 'object' && s.label != null && s.value != null) {
        return { label: String(s.label), value: String(s.value) };
      }
      return null;
    })
    .filter(Boolean);
  return out.length ? out : [{ label: '', value: '' }];
}
